import React, { useMemo } from 'react';
import styles from './M11Hero.module.scss';
import { Image, Link, Video } from '~/shared/components';
import { ModuleContainer } from '../ModuleContainer';
import { ButtonColorProps, M11HeroModule } from '~/lib';
import { Button } from '~/shared/components/Button/Button';
import clsx from 'clsx';
import { Container } from '~/shared/components/Container';
import { WaterMarkLogo, WaterMarkPercent } from './WaterMark/WaterMarks';
import Typography, { TypographyStyle } from '~/shared/components/Typography/Typography';

interface CustomCSSProperties extends React.CSSProperties {
    [key: `--${string}`]: string | number;
}

export const M11Hero: React.FC<M11HeroModule> = ({
    trumpet,
    title,
    paragraphText,
    link,
    image,
    imageAltText,
    colorTheme,
    imageOnBackground,
    watermark,
    spacingBottom,
    spacingTop,
    disclaimer,
    titleSize,
    video,
    ...rest
}) => {
    const { mainColor, highlightColor, foregroundColor, secondaryColor, secondaryColorOpacity } =
        colorTheme;

    const headingVariant = useMemo<TypographyStyle>(() => {
        switch (titleSize) {
            case 'Large':
                return 'display-L';
            case 'Extra Large':
                return 'display-XL';
        }

        return 'title-L';
    }, [titleSize]);

    const buttonVariant = useMemo<ButtonColorProps>(() => {
        const whiteValues = ['#fff', 'white', '#ffffff'];

        if (whiteValues.includes(foregroundColor.toLowerCase())) return 'primary-inverse';

        return 'primary';
    }, [foregroundColor]);

    const watermarkElement = useMemo(() => {
        if (!watermark) return null;

        return watermark === 'Logo' ? <WaterMarkLogo /> : <WaterMarkPercent />;
    }, [watermark]);

    const noMedia = !image?.src && !video;

    const memoizedCustomProperties = useMemo<CustomCSSProperties>(
        () => ({
            '--color-main': mainColor,
            '--color-foreground': foregroundColor,
            '--color-watermark-bg': secondaryColor,
            '--color-watermark-opacity': secondaryColorOpacity,
            '--color-image-shadow': highlightColor,
            '--color-bullet-color': mainColor === '#f68a20' ? foregroundColor : '#f68a20',
        }),
        [],
    );

    return (
        <ModuleContainer fullWidth {...rest} style={memoizedCustomProperties}>
            <Container>
                <div
                    className={clsx(styles.wrapperHero, {
                        [styles.noImage]: noMedia,
                    })}
                >
                    <div
                        className={clsx(styles.background, {
                            [styles.noImage]: noMedia,
                        })}
                    >
                        {!imageOnBackground && watermark && (
                            <div
                                className={clsx(styles.watermark, {
                                    [styles.watermarkLogo]: watermark === 'Logo',
                                    [styles.watermarkPercent]: watermark == 'Percentage',
                                    [styles.waterMarkNoImg]: noMedia,
                                })}
                            >
                                {watermarkElement}
                            </div>
                        )}
                    </div>
                    <div
                        className={clsx(styles.heroContainer, {
                            [styles.noImage]: noMedia,
                        })}
                    >
                        <div
                            className={clsx(styles.content, {
                                [styles.noImage]: noMedia,
                            })}
                        >
                            {trumpet && <span className={styles.trumpet}>{trumpet}</span>}

                            {title && (
                                <Typography
                                    className={clsx(styles.title, {
                                        [styles.noParagraph]: !paragraphText,
                                        [styles.noSpace]: !paragraphText || !link?.text,
                                        [styles.withButton]: !paragraphText && link?.text,
                                    })}
                                    variant={headingVariant}
                                    as="h2"
                                >
                                    <span>{title}</span>
                                </Typography>
                            )}

                            {paragraphText && (
                                <div
                                    className={clsx(styles.paragraph, {
                                        [styles.noSpace]: !link?.text,
                                    })}
                                    dangerouslySetInnerHTML={{ __html: paragraphText }}
                                />
                            )}

                            {link?.text && link?.url && (
                                <Button
                                    variant={buttonVariant}
                                    title={link.title}
                                    as={Link}
                                    href={link.url}
                                    children={link.text}
                                    className={styles.cta}
                                />
                            )}
                        </div>

                        {(image?.src || video?.src) && (
                            <div className={styles.media}>
                                <div
                                    className={clsx(styles.mediaWrapper, {
                                        [styles.isVerticalOnMobile]: !!video?.src,
                                    })}
                                >
                                    {imageOnBackground && (
                                        <div
                                            className={clsx(styles.watermark, {
                                                [styles.watermarkPercentMobile]:
                                                    imageOnBackground && watermark === 'Percent',
                                                [styles.watermarkLogoImage]:
                                                    imageOnBackground && watermark === 'Logo',
                                            })}
                                        >
                                            {watermarkElement}
                                        </div>
                                    )}

                                    <div className={styles.shadow} />

                                    {video ? (
                                        <div
                                            className={clsx(styles.mediaElement, {
                                                [styles.imageOnBackground]: imageOnBackground,
                                            })}
                                        >
                                            <Video
                                                name={title}
                                                muted={video.mute}
                                                controls={false}
                                                src={video.src}
                                                loop={video.loop}
                                                playing={video.autoPlay}
                                                posterSrc={image?.src}
                                                cover={true}
                                                theaterMode={video.theaterMode}
                                            />
                                        </div>
                                    ) : (
                                        <Image
                                            src={image.src}
                                            alt={imageAltText}
                                            className={clsx(styles.mediaElement, {
                                                [styles.imageOnBackground]: imageOnBackground,
                                            })}
                                            layout="fill"
                                            objectFit={imageOnBackground ? 'contain' : 'cover'}
                                            objectPosition="center"
                                        />
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    {disclaimer && (
                        <div
                            className={clsx(styles.disclaimer, {
                                [styles.noImage]: noMedia,
                            })}
                            dangerouslySetInnerHTML={{ __html: disclaimer }}
                        />
                    )}
                </div>
            </Container>
        </ModuleContainer>
    );
};
